import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ticketStatuses, type TicketStatus } from '@ticketai/core';
import { ErrorAlert } from '@/components/ErrorAlert';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDateTime, ticketLabel } from '@/lib/format';
import { client } from '@/lib/orpc';
import { useMe } from '@/lib/session';

export const Route = createFileRoute('/_app/tickets/')({
  validateSearch: z.object({
    status: z.enum(ticketStatuses).optional(),
    mine: z.boolean().optional(),
    q: z.string().max(200).optional()
  }),
  component: TicketsPage
});

function TicketsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [q, setQ] = useState(search.q ?? '');

  // Debounced search into the URL (shareable, back button works).
  useEffect(() => {
    const h = setTimeout(() => {
      if ((search.q ?? '') !== q) void navigate({ search: (s) => ({ ...s, q: q || undefined }), replace: true });
    }, 300);
    return () => clearTimeout(h);
  }, [q, search.q, navigate]);

  const list = useInfiniteQuery({
    queryKey: ['tickets', search, me.id],
    initialPageParam: undefined as number | undefined,
    refetchInterval: 30_000,
    queryFn: ({ pageParam }) =>
      client.tickets.list({
        limit: 30,
        ...(search.status && { statuses: [search.status] }),
        ...(search.mine && { participantId: me.id }),
        ...(search.q && { q: search.q }),
        ...(pageParam && { before: pageParam })
      }),
    getNextPageParam: (last) => last.nextBefore ?? undefined
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div>
      <PageHeader title={t('tickets.title')} lead={t('tickets.lead')} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input className="input input-sm w-64" type="search" placeholder={t('tickets.search')} aria-label={t('tickets.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        <select
          className="select select-sm w-auto"
          aria-label={t('tickets.status')}
          value={search.status ?? ''}
          onChange={(e) => void navigate({ search: (s) => ({ ...s, status: (e.target.value || undefined) as TicketStatus | undefined }) })}
        >
          <option value="">{t('common.all')}</option>
          {ticketStatuses.map((s) => (
            <option key={s} value={s}>
              {t(`status.${s}`)}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="toggle toggle-sm" checked={!!search.mine} onChange={(e) => void navigate({ search: (s) => ({ ...s, mine: e.target.checked || undefined }) })} />
          {t('tickets.mine')}
        </label>
      </div>

      {list.isError && <ErrorAlert error={list.error} onRetry={() => void list.refetch()} />}
      {list.isPending && <span className="loading loading-spinner" />}
      {list.isSuccess && !items.length && <p className="text-base-content/60">{t('tickets.empty')}</p>}
      {items.length > 0 && (
        <div className="bg-base-100 rounded-box overflow-x-auto shadow-sm">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t('tickets.title')}</th>
                <th>{t('tickets.status')}</th>
                <th>{t('tickets.requester')}</th>
                <th>{t('tickets.assignee')}</th>
                <th>{t('tickets.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((tk) => (
                <tr key={tk.id} className="hover:bg-base-200" data-testid="ticket-row">
                  <td className="font-mono text-xs whitespace-nowrap">{ticketLabel(tk.number)}</td>
                  <td>
                    <Link to="/tickets/$ref" params={{ ref: ticketLabel(tk.number) }} className="link link-hover font-medium">
                      {tk.title}
                    </Link>
                    {tk.needsHuman.length > 0 && <span className="badge badge-warning badge-xs ml-2">{t('tickets.needsHuman')}</span>}
                    {tk.labels.map((l) => (
                      <span key={l} className="badge badge-ghost badge-xs ml-1">
                        {l}
                      </span>
                    ))}
                  </td>
                  <td>
                    <StatusBadge status={tk.status} />
                  </td>
                  <td className="whitespace-nowrap">{tk.requesterName}</td>
                  <td className="whitespace-nowrap">{tk.assigneeName}</td>
                  <td className="text-base-content/60 text-xs whitespace-nowrap">{formatDateTime(tk.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.hasNextPage && (
        <button type="button" className="btn btn-sm mt-4" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
          {t('common.loadMore')}
        </button>
      )}
    </div>
  );
}
